import React from "react";
import { Box, Typography } from "@mui/material";
import CollectionsIcon from "@mui/icons-material/Collections";
import { styled } from "@mui/material/styles";

const MediaContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  aspectRatio: "16/9",
  position: "relative",
  overflow: "hidden",
  borderRadius: "16px 16px 0 0",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(135deg, rgba(248,250,252,0.8) 0%, rgba(255,255,255,0.6) 100%)",
  border: "1px solid rgba(226,232,240,0.3)",
}));

const UserInfoBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "8px",
  left: "8px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  background: "linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(139,92,246,0.15) 100%)",
  padding: "6px 10px",
  borderRadius: "12px",
  border: "1px solid rgba(255,255,255,0.3)",
  boxShadow: "0 4px 20px rgba(99,102,241,0.2), inset 0 1px 0 rgba(255,255,255,0.2)",
  backdropFilter: "blur(25px)",
  WebkitBackdropFilter: "blur(25px)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  maxWidth: "200px",
  "&:hover": {
    transform: "translateY(-2px)",
    background: "linear-gradient(135deg, rgba(99,102,241,0.25) 0%, rgba(139,92,246,0.25) 100%)",
    boxShadow: "0 8px 30px rgba(99,102,241,0.3), inset 0 1px 0 rgba(255,255,255,0.3)",
    borderColor: "rgba(255,255,255,0.4)",
  },
}));

const UserAvatar = styled(Box)(({ theme }) => ({
  width: "24px",
  height: "24px",
  borderRadius: "50%",
  border: "1px solid rgba(255,255,255,0.4)",
  background: "linear-gradient(135deg, rgba(99,102,241,0.8) 0%, rgba(139,92,246,0.8) 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  boxShadow: "0 2px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)",
}));

const UserNameText = styled(Typography)(({ theme }) => ({
  fontSize: "11px",
  fontWeight: 700,
  color: "rgba(255,255,255,0.95)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "150px",
  textShadow: "0 1px 2px rgba(0,0,0,0.3)",
}));

const CollectionLabel = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: "8px",
  left: "8px",
  color: "white",
  background: "linear-gradient(135deg, rgba(139,92,246,0.9) 0%, rgba(168,85,247,0.9) 100%)",
  padding: "4px 8px",
  borderRadius: "10px",
  fontSize: "10px",
  fontWeight: 600,
  maxWidth: "60%",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  border: "1px solid rgba(255,255,255,0.2)",
  boxShadow: "0 2px 8px rgba(139,92,246,0.3)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "4px",
  "&:hover": {
    background: "linear-gradient(135deg, rgba(124,58,237,0.95) 0%, rgba(147,51,234,0.95) 100%)",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(139,92,246,0.4)",
  },
}));

const ItemCountLabel = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: "8px",
  right: "8px",
  background: "linear-gradient(135deg, rgba(16,185,129,0.9) 0%, rgba(34,197,94,0.9) 100%)",
  padding: "4px 8px",
  borderRadius: "10px",
  fontSize: "11px",
  fontWeight: 600,
  color: "white",
  border: "1px solid rgba(255,255,255,0.2)",
  boxShadow: "0 2px 8px rgba(16,185,129,0.3)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  display: "flex",
  alignItems: "center",
  gap: "4px",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(16,185,129,0.4)",
  },
}));

const FallbackContainer = styled(Box)(({ theme, color }) => ({
  width: "100%",
  aspectRatio: "16/9",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
  color: "#fff",
  fontSize: "48px",
  fontWeight: "bold",
  borderRadius: "16px 16px 0 0",
  position: "relative",
  overflow: "hidden",
  border: "1px solid rgba(226,232,240,0.3)",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255,255,255,0.1)",
    backdropFilter: "blur(10px)",
  },
}));

const FallbackLetter = styled(Typography)(({ theme }) => ({
  fontSize: "48px",
  fontWeight: "bold",
  textShadow: "0 4px 8px rgba(0,0,0,0.3)",
  zIndex: 1,
  position: "relative",
}));

function getColorForCollection(data) {
  const colors = [
    "#6366f1", // Indigo
    "#8b5cf6", // Violet
    "#06b6d4", // Cyan
    "#10b981", // Emerald
    "#f59e0b", // Amber
    "#ef4444", // Red
  ];

  const name = data?.name || "";
  const firstLetter = name[0]?.toLowerCase() || "a";
  return colors[firstLetter.charCodeAt(0) % colors.length];
}

const CollectionCardMedia = ({ data, minHeight, maxHeight }) => {
  const color = getColorForCollection(data);

  const handleUserClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Navigate to user profile - prefer user_name over user_id
    if (data?.user_name) {
      window.location.href = `/user/${data.user_name}`;
    } else if (data?.user_id) {
      window.location.href = `/user/${data.user_id}`;
    }
  };

  // If there is an image_url
  if (data?.img_url) {
    return (
      <MediaContainer>
        <div
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "16px 16px 0 0",
            background: "linear-gradient(135deg, rgba(248,250,252,0.9) 0%, rgba(255,255,255,0.8) 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <img
            src={data.img_url}
            alt={data.name || "Collection image"}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
        </div>
        {/* User info overlay */}
        <UserInfoBox onClick={handleUserClick}>
          <UserAvatar>
            {data.profile_image_url ? (
              <img
                src={data.profile_image_url}
                alt={data.user_name || "Profile Picture"}
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <Typography sx={{ fontSize: "12px", fontWeight: "bold", color: "white" }}>
                {data?.user_name?.[0]?.toUpperCase() || "?"}
              </Typography>
            )}
          </UserAvatar>
          <UserNameText>
            {data.display_name || data.first_name && data.last_name ? `${data.first_name} ${data.last_name}` : data.first_name || data.last_name || data.user_name || "Anonymous User"}
          </UserNameText>
        </UserInfoBox>
      </MediaContainer>
    );
  }

  // Fallback with color and first letter
  const letter = data?.name?.[0]?.toUpperCase() || "?";
  const displayName = data.display_name || (data.first_name && data.last_name ? `${data.first_name} ${data.last_name}` : data.first_name || data.last_name || data.user_name || "Anonymous User");
  return (
    <FallbackContainer color={color}>
      {/* User info overlay */}
      <UserInfoBox onClick={handleUserClick}>
        <UserAvatar>
          {data.profile_image_url ? (
            <img
              src={data.profile_image_url}
              alt={displayName || "Profile Picture"}
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
          ) : (
            <Typography sx={{ fontSize: "12px", fontWeight: "bold", color: "white" }}>
              {data?.first_name?.[0]?.toUpperCase() || data?.last_name?.[0]?.toUpperCase() || data?.user_name?.[0]?.toUpperCase() || "?"}
            </Typography>
          )}
        </UserAvatar>
        <UserNameText>
          {displayName}
        </UserNameText>
      </UserInfoBox>
      <FallbackLetter>{letter}</FallbackLetter>
    </FallbackContainer>
  );
};

export default CollectionCardMedia;
